import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export type NotificationType = 'success' | 'error' | 'warning' | 'info';

/** Structured validation error for better UI display */
export interface ValidationErrorItem {
  type: string;
  message: string;
  path?: string;
  severity?: string;
}

export interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  /** Optional detailed content (e.g., JSON validation result) - legacy */
  details?: string;
  /** Structured validation errors for better UI display */
  validationErrors?: ValidationErrorItem[];
  timestamp: number;
  read: boolean;
  /** Whether the notification has been shown as a toast */
  toasted?: boolean;
  /** Optional action button shown in the toast (e.g., "Undo"). Not persisted — callback is lost on page reload. */
  actionButton?: {
    label: string;
    onClick: () => void;
  };
  /** Override the default toast auto-dismiss duration (ms). If not set, uses the type-based default. */
  durationMs?: number;
}

interface NotificationState {
  notifications: Notification[];
  unreadCount: number;
  
  // Actions
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp' | 'read' | 'toasted'>) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  removeNotification: (id: string) => void;
  clearAllNotifications: () => void;
  markAsToasted: (id: string) => void;
}

export const useNotificationStore = create<NotificationState>()(
  persist(
    (set) => ({
      notifications: [],
      unreadCount: 0,

      addNotification: (notification) => {
        const newNotification: Notification = {
          ...notification,
          id: `notification-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
          timestamp: Date.now(),
          read: false,
          toasted: false,
        };

        set((state) => ({
          notifications: [newNotification, ...state.notifications].slice(0, 50), // Keep max 50 notifications
          unreadCount: state.unreadCount + 1,
        }));
      },

      markAsRead: (id) => {
        set((state) => {
          const notification = state.notifications.find((n) => n.id === id);
          if (!notification || notification.read) {
            return state;
          }
          return {
            notifications: state.notifications.map((n) =>
              n.id === id ? { ...n, read: true } : n
            ),
            unreadCount: Math.max(0, state.unreadCount - 1),
          };
        });
      },

      markAllAsRead: () => {
        set((state) => ({
          notifications: state.notifications.map((n) => ({ ...n, read: true })),
          unreadCount: 0,
        }));
      },
      
      markAsToasted: (id) => {
        set((state) => ({
          notifications: state.notifications.map((n) =>
            n.id === id ? { ...n, toasted: true } : n
          ),
        }));
      },

      removeNotification: (id) => {
        set((state) => {
          const notification = state.notifications.find((n) => n.id === id);
          const wasUnread = notification && !notification.read;
          return {
            notifications: state.notifications.filter((n) => n.id !== id),
            unreadCount: wasUnread ? Math.max(0, state.unreadCount - 1) : state.unreadCount,
          };
        });
      },

      clearAllNotifications: () => {
        set({ notifications: [], unreadCount: 0 });
      },
    }),
    {
      name: 'otto-notifications',
      storage: createJSONStorage(() => sessionStorage),
      // Strip actionButton (contains non-serializable onClick) from persisted state.
      // On rehydration, action buttons are intentionally absent — the undo opportunity has passed.
      partialize: (state) => ({
        ...state,
        notifications: state.notifications.map(
          ({ actionButton: _ab, ...rest }) => rest as Notification,
        ),
      }),
    }
  )
);
